import { useState } from "react";
import { X, ArrowLeft, ArrowRight, Mail, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

/* ---------- Projects (no names/summaries; grouped folders) ---------- */
const projects = [
  {
    id: "bath-1",
    category: "bathroom",
    location: "Portsmouth",
    images: ["/portfolio/p2.jpg", "/portfolio/p4.jpg"],
  },
  {
    id: "bath-2",
    category: "bathroom",
    location: "Hambledon",
    images: ["/portfolio/p1.jpg", "/portfolio/p3.jpg", "/portfolio/p8.jpg"],
  },
  {
    id: "bath-3",
    category: "bathroom",
    location: "Locks Heath",
    images: [
      "/portfolio/p9.jpg",
      "/portfolio/p10.jpg",
      "/portfolio/p11.jpg",
      "/portfolio/p12.jpg",
      "/portfolio/p13.jpg",
      "/portfolio/p14.jpg",
    ],
  },
  {
    id: "bath-5",
    category: "bathroom",
    location: "Hedge End",
    images: ["/portfolio/p16.jpg", "/portfolio/p17.jpg", "/portfolio/p15.jpg"],
  },
  {
    id: "bath-7",
    category: "bathroom",
    location: "Otterbourne",
    images: ["/portfolio/p18.jpg", "/portfolio/p19.jpg"],
  },
  {
    id: "bath-8",
    category: "bathroom",
    location: "Gosport",
    images: ["/portfolio/p20.jpg"],
  },
  {
    id: "bath-9",
    category: "bathroom",
    location: "Whiteley",
    images: [
      "/portfolio/p21.jpg",
      "/portfolio/p34.jpg",
      "/portfolio/p35.jpg",
      "/portfolio/p36.jpg",
      "/portfolio/p37.jpg",
      "/portfolio/p38.jpg",
      "/portfolio/p39.jpg",
      "/portfolio/p40.jpg",
      "/portfolio/p41.jpg",
    ],
  },
  {
    id: "bath-10",
    category: "bathroom",
    location: "Hambledon",
    images: ["/portfolio/p22.jpg", "/portfolio/p42.jpg"],
  },
  {
    id: "bath-11",
    category: "bathroom",
    location: "Gosport",
    images: ["/portfolio/p25.jpg", "/portfolio/p26.jpg", "/portfolio/p59.jpg", "/portfolio/p60.jpg"],
  },
  {
    id: "bath-12",
    category: "bathroom",
    location: "Gosport",
    images: ["/portfolio/p27.jpg"],
  },
  {
    id: "bath-13",
    category: "bathroom",
    location: "Southampton",
    images: [
      "/portfolio/p28.jpg",
      "/portfolio/p29.jpg",
      "/portfolio/p30.jpg",
      "/portfolio/p31.jpg",
      "/portfolio/p32.jpg",
      "/portfolio/p33.jpg",
    ],
  },
  {
    id: "bath-14",
    category: "bathroom",
    location: "Southsea",
    images: [
      "/portfolio/p43.jpg",
      "/portfolio/p44.jpg",
      "/portfolio/p45.jpg",
    ],
  },
  {
    id: "bath-15",
    category: "bathroom",
    location: "Southampton",
    images: [
      "/portfolio/p46.jpg",
      "/portfolio/p47.jpg",
      "/portfolio/p48.jpg",
      "/portfolio/p49.jpg",
      "/portfolio/p50.jpg",
      "/portfolio/p51.jpg",
      "/portfolio/p52.jpg",
    ],
  },
  {
    id: "bath-16",
    category: "bathroom",
    location: "Titchfield",
    images: [
      "/portfolio/p53.jpg",
      "/portfolio/p54.jpg",
      "/portfolio/p55.jpg",
      "/portfolio/p56.jpg",
    ],
  },
  {
    id: "bath-17",
    category: "bathroom",
    location: "Chandler's Ford",
    images: ["/portfolio/p57.jpg", "/portfolio/p58.jpg"],
  },
  {
    id: "bath-18",
    category: "bathroom",
    location: "Locks Heath",
    images: [
      "/portfolio/p61.jpg",
      "/portfolio/p62.jpg",
      "/portfolio/p63.jpg",
      "/portfolio/p64.jpg",
    ],
  },
  {
    id: "bath-19",
    category: "bathroom",
    location: "Whiteley",
    images: ["/portfolio/p65.jpg", "/portfolio/p66.jpg", "/portfolio/p67.jpg"],
  },
  {
    id: "kitchen-1",
    category: "kitchen",
    location: "Southampton",
    images: [
      "/portfolio/p6.jpg",
      "/portfolio/p7.jpg",
      "/portfolio/p5.jpg",
      "/portfolio/p23.jpg",
      "/portfolio/p24.jpg",
    ],
  },
];

const filters = [
  { id: "all", label: "All Projects" },
  { id: "bathroom", label: "Bathrooms" },
  { id: "kitchen", label: "Kitchens" },
];

/* ---------- Lightbox ---------- */
function ProjectLightbox({ project, open, onClose }) {
  const [idx, setIdx] = useState(0);
  if (!project) return null;
  const total = project.images.length;
  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setIdx(0);
          onClose();
        }
      }}
    >
      <DialogContent
        data-testid="portfolio-lightbox"
        className="max-w-6xl w-[95vw] p-0 bg-[#1C2A39] border-[#1C2A39] rounded-none overflow-hidden"
      >
        <VisuallyHidden>
          <DialogTitle>Project photo {idx + 1} of {total}</DialogTitle>
          <DialogDescription>Full-size view of a previous C4 installation</DialogDescription>
        </VisuallyHidden>
        <div className="relative">
          <img
            src={project.images[idx]}
            alt=""
            className="w-full h-[60vh] lg:h-[85vh] object-contain bg-[#1C2A39]"
          />
          {project.location && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 text-[11px] font-heading font-semibold tracking-wide text-[#1C2A39]">
              {project.location}
            </div>
          )}
          {total > 1 && (
            <>
              <button
                onClick={prev}
                data-testid="lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/90 hover:bg-white text-[#1C2A39] transition-colors"
                aria-label="Previous"
              >
                <ArrowLeft size={18} strokeWidth={1.5} />
              </button>
              <button
                onClick={next}
                data-testid="lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/90 hover:bg-white text-[#1C2A39] transition-colors"
                aria-label="Next"
              >
                <ArrowRight size={18} strokeWidth={1.5} />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {project.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={cn(
                      "h-1 transition-all",
                      i === idx ? "w-8 bg-[#C9A24A]" : "w-4 bg-white/40"
                    )}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 grid place-items-center bg-white/90 hover:bg-white text-[#1C2A39] transition-colors"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
          <div className="absolute bottom-4 right-4 px-3 py-1 bg-white/90 text-[11px] font-heading font-semibold text-[#1C2A39]">
            {idx + 1} / {total}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Main Section ---------- */
export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible = projects.filter(
    (p) => filter === "all" || p.category === filter
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-24 lg:py-32 bg-white border-y border-[#E8E5DD]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <span className="uppercase text-xs tracking-[0.3em] text-[#C9A24A] font-semibold">
              Our Previous Works
            </span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {filters.map((f) => (
              <button
                key={f.id}
                data-testid={`portfolio-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "h-10 px-5 text-sm font-heading font-semibold tracking-wide border transition-colors",
                  filter === f.id
                    ? "bg-[#1C2A39] text-white border-[#1C2A39]"
                    : "bg-transparent text-[#1C2A39] border-[#E8E5DD] hover:border-[#C9A24A]"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <div
            data-testid="portfolio-empty-state"
            className="border border-dashed border-[#E8E5DD] py-20 px-8 text-center bg-[#F9F8F6]/60"
          >
            <h3 className="font-heading text-2xl text-[#1C2A39] font-medium">
              No projects in this category yet.
            </h3>
            <p className="mt-3 text-sm text-[#5C6670] max-w-md mx-auto leading-relaxed">
              We&apos;re collating more project photography. In the meantime, get
              in touch and we&apos;ll happily share recent examples privately.
            </p>
            <Button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="mt-7 bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-11 px-6 font-heading font-semibold"
            >
              <Mail size={16} strokeWidth={1.75} className="mr-2" />
              Request project examples
            </Button>
          </div>
        ) : (
          <div
            data-testid="portfolio-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
          >
            {visible.map((project) => (
              <button
                key={project.id}
                data-testid={`portfolio-card-${project.id}`}
                onClick={() => setActive(project)}
                className="group relative overflow-hidden bg-[#1C2A39] aspect-[4/5]"
              >
                <img
                  src={project.images[0]}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-[#1C2A39]/0 group-hover:bg-[#1C2A39]/20 transition-colors" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-white/40 transition-all" />
                <div className="absolute top-4 left-4 px-3 py-1 bg-white/95 text-[10px] font-heading font-semibold tracking-[0.25em] uppercase text-[#1C2A39]">
                  {project.category}
                </div>
                {project.location && (
                  <div className="absolute bottom-4 left-4 px-3 py-1 bg-[#1C2A39]/90 text-[11px] font-heading font-semibold tracking-wide text-white">
                    {project.location}
                  </div>
                )}
                <div className="absolute top-4 right-4 px-3 py-1 bg-[#C9A24A] text-white text-[11px] font-heading font-semibold flex items-center gap-1.5">
                  <Images size={12} strokeWidth={1.75} />
                  {project.images.length}
                </div>
              </button>
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-xs text-[#5C6670]">
          Want to see more? We can share additional project examples on request —{" "}
          <button
            onClick={() =>
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="underline underline-offset-4 hover:text-[#C9A24A]"
          >
            get in touch
          </button>
          .
        </p>
      </div>

      <ProjectLightbox
        project={active}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </section>
  );
}
